import { type ClockMeridiem, type DatePatternNames, type Locale } from '@aysar/contracts';

/** A month with 31 days in a year with no calendar oddities — any would do. */
const REFERENCE_YEAR = 2026;
/** 2026-02-01 was a Sunday, so seven consecutive days start the week correctly. */
const REFERENCE_SUNDAY = Date.UTC(2026, 1, 1);
const DAY_MS = 24 * 60 * 60 * 1000;

const cache = new Map<Locale, DatePatternNames>();

function namesOf(locale: Locale, options: Intl.DateTimeFormatOptions, dates: number[]): string[] {
  const format = new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
  return dates.map((stamp) => format.format(new Date(stamp)));
}

/**
 * The month and weekday names `formatDatePattern` needs, for one locale.
 *
 * The formatter itself lives in `@aysar/contracts` and holds **no** locale data
 * — it is imported by the api, the admin and React Native, and Hermes ships a
 * reduced ICU — so each caller supplies the two lists. This is the admin's,
 * built from `Intl` and cached per locale: without the cache the preview would
 * construct nineteen `Intl.DateTimeFormat`s on every keystroke.
 *
 * The weekday list starts on a **Sunday**, matching `DatePatternParts.weekday`
 * and `Date.prototype.getDay`. Starting it on a Monday — as several locales
 * display it — would render every weekday one day out.
 */
export function patternNamesFor(locale: Locale): DatePatternNames {
  const cached = cache.get(locale);
  if (cached !== undefined) return cached;

  const months = Array.from({ length: 12 }, (_unused, index) => Date.UTC(REFERENCE_YEAR, index, 1));
  const weekdays = Array.from({ length: 7 }, (_unused, index) => REFERENCE_SUNDAY + index * DAY_MS);

  const names: DatePatternNames = {
    months: namesOf(locale, { month: 'long' }, months),
    monthsShort: namesOf(locale, { month: 'short' }, months),
    weekdays: namesOf(locale, { weekday: 'long' }, weekdays),
    weekdaysShort: namesOf(locale, { weekday: 'short' }, weekdays),
  };

  cache.set(locale, names);
  return names;
}

const meridiemCache = new Map<Locale, ClockMeridiem>();

/**
 * The day-period markers `formatClock` renders a 12-hour time with, for one
 * locale — `AM`/`PM`, `ص`/`م` — read from `Intl` rather than written down, so
 * no locale's marker is a literal in this bundle (P28).
 */
export function meridiemFor(locale: Locale): ClockMeridiem {
  const cached = meridiemCache.get(locale);
  if (cached !== undefined) return cached;

  const format = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    hour12: true,
    timeZone: 'UTC',
  });
  const periodOf = (hour: number): string =>
    format
      .formatToParts(new Date(Date.UTC(REFERENCE_YEAR, 0, 1, hour)))
      .find((part) => part.type === 'dayPeriod')?.value ?? '';

  const meridiem: ClockMeridiem = { am: periodOf(9), pm: periodOf(15) };
  meridiemCache.set(locale, meridiem);
  return meridiem;
}
